
import React, { useState, useRef, useEffect } from 'react';
import {
  mergeClasses,
  Button,
  Textarea,
} from '@fluentui/react-components';
import { ToggleButtons } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import {
  ArrowClockwise16Regular,
} from '@fluentui/react-icons';
import { getOptions } from './options';
import { usePromptEditorBaseStyles, usePromptEditorStyles } from './style';

const displayCount = 5;

const sliceOptions = (options, index, usedOptions) => {
  const filteredOptions = options.filter(option => !usedOptions.find(usedOption => usedOption.key === option.key));
  const roundedIndex = index % filteredOptions.length;

  let slicedOptions = filteredOptions.slice(roundedIndex, roundedIndex + displayCount);


  if (slicedOptions.length < displayCount) {
    slicedOptions = [...slicedOptions, ...filteredOptions.slice(0, (Math.min(displayCount, filteredOptions.length) - slicedOptions.length))];
  }

  return slicedOptions;
};

export const PromptEditor = (props) => {
  const {
    i18n,
    prompt,
    setPrompt,
    logger,
    textAreaRef: textArea,
  } = props;

  const [usedOptions, setUsedOptions] = useState([]);
  const options = getOptions(i18n, usedOptions);
  const promptEditorBaseStyles = usePromptEditorBaseStyles();
  const promptEditorStyles = usePromptEditorStyles();
  const [optionsState, setOptionsState] = useState({});
  const selectedOption = options.find(option => optionsState[option.key]);
  const [textToComplete, setTextToComplete] = useState('');
  const [optionsIndex, setOptionsIndex] = useState(0);

  const nextOption = () => {
    setOptionsIndex((optionsIndex + displayCount));
  };

  const slicedOptions = selectedOption && selectedOption.words.length > 0
    ? sliceOptions(selectedOption.words, optionsIndex, usedOptions)
    : [];

  const shadowTextArea = useRef();
  const wordsRef = useRef();

  useEffect(() => {
    // sync the scroll position of the shadow textarea with the main textarea
    if (textArea && textArea.current && shadowTextArea.current) {
      if (textToComplete === '') {
        shadowTextArea.current.style.visibility = 'hidden';
        shadowTextArea.current.style.overflowY = '';
        textArea.current.style.overflowY = '';
      } else if (textArea && textArea.current && textArea.current.scrollHeight > textArea.current.clientHeight) {
      // scrollbar of the textarea is visible
        shadowTextArea.current.style.overflowY = 'scroll';
        shadowTextArea.current.style.visibility = '';
        shadowTextArea.current.scrollTop = textArea.current.scrollTop;
      } else if (shadowTextArea.current.scrollHeight > shadowTextArea.current.clientHeight) {
      // scrollbar of the shadow textarea is visible
        shadowTextArea.current.style.visibility = '';
        textArea.current.style.overflowY = 'scroll';
        textArea.current.scrollTop = shadowTextArea.current.scrollTop;
      } else {
        shadowTextArea.current.style.overflowY = '';
        shadowTextArea.current.style.visibility = '';
        textArea.current.style.overflowY = '';
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textArea, shadowTextArea.current, textToComplete]);

  return (
    <div className={mergeClasses('prompt-editor', promptEditorBaseStyles)}>
      <Textarea
        ref={shadowTextArea}
        className={
          mergeClasses('shadow-textarea', 'prompt', promptEditorStyles.autoCompleteTextarea)}
        value={textToComplete && textToComplete.length > 0 && prompt && prompt.length > 0 ?
          `${prompt}${prompt[prompt.length - 1] === ' ' ? '' : ' '}${textToComplete}` : ''}
      />
      <Textarea
        ref={textArea}
        className={
          prompt && prompt.length > 0 ?
            mergeClasses('main-textarea', 'prompt', promptEditorStyles.textarea) :
            mergeClasses('main-textarea', 'prompt', promptEditorStyles.textareaPlaceHolder)}
        placeholder={i18n.getString(_TL_('What image do you want to create? Describe the subject, key elements, place, style, lighting, and/or mood.'))}
        value={prompt}
        onChange={(e, data) => setPrompt(data.value)}
      />
      <div className="words" ref={wordsRef}>
        {selectedOption
          &&
          selectedOption.words.length > 0
          &&
            <>
              {slicedOptions.map(word => (
                <Button
                  key={word.key}
                  className="suggested-word"
                  onClick={() => {
                    if (logger && logger.logTrace) {
                      logger.logTrace('AIgeneratedImage: prompt button clicked', { word: word.word });
                    }
                    setPrompt(`${prompt}${((prompt === '') || (prompt && prompt.length > 0 && prompt[prompt.length - 1] === ' ')) ? '' : ' '}${word.word}`);
                    setUsedOptions([...usedOptions, word]);
                    setTextToComplete('');

                    if (textArea && textArea.current) {
                      textArea.current.focus();
                    }
                  }}
                  onMouseOver={() => {
                    if (textToComplete !== word.word) {
                      setTextToComplete(word.word);
                    }
                  }}
                  onMouseOut={() => setTextToComplete('')}
                >
                  {word.word}
                </Button>
              ))}
              <Button icon={<ArrowClockwise16Regular />} onClick={nextOption} className="refresh" aria-label={i18n.getString(_TL_('Refresh'))} />
            </>
          }
      </div>
      <div className="buttons">
        <ToggleButtons
          options={options}
          classes={promptEditorStyles}
          optionsState={optionsState}
          toggleSelectedOption={(key) => {
            if (logger && logger.logTrace) {
              logger.logTrace('AIgeneratedImage: toggle button clicked', { key });
            }
            setOptionsIndex(0);
            setOptionsState({
              ...(options.map(option => ({ [option.key]: false }))),
              [key]: !optionsState[key],
            });

            if (!optionsState[key]) {
              if (wordsRef && wordsRef.current) {
                setTimeout(() => {
                  const suggestedWords = wordsRef.current.querySelectorAll('.suggested-word');
                  if (suggestedWords && suggestedWords.length > 0) {
                    suggestedWords[0].focus();
                  }
                });
              }
            }
          }}
        />
      </div>
    </div>);
};
